import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Building2, Loader2, Check, LocateFixed } from "lucide-react";
import { toast } from "sonner";

export default function AddressAutocomplete({ label, value, onSelect, testid, onQueryChange, placeholder, initialQuery }) {
  const [mode, setMode] = useState("address");
  const [query, setQuery] = useState(initialQuery || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState(value || null);
  const boxRef = useRef(null);
  const skipSearch = useRef(false);
  const [locating, setLocating] = useState(false);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location isn't supported on this device"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.get("/address/reverse", { params: { lat: pos.coords.latitude, lng: pos.coords.longitude } });
        skipSearch.current = true;
        setSelected(data);
        setQuery(data.label);
        setOpen(false);
        onSelect(data);
        toast.success("Location found — double-check the address looks right");
      } catch {
        toast.error("Couldn't work out the address at your location");
      } finally { setLocating(false); }
    }, () => { toast.error("Location permission denied — allow it in your browser to use GPS"); setLocating(false); },
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 });
  };

  // Reflect an externally-provided value (e.g. from label-photo scan) without re-triggering search
  useEffect(() => {
    if (value && value.label) { skipSearch.current = true; setSelected(value); setQuery(value.label); setOpen(false); }
  }, [value]);

  useEffect(() => {
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (skipSearch.current) { skipSearch.current = false; return; }
    if (!query || query.length < 3) { setResults([]); setSearched(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/address/autocomplete`, { params: { q: query, mode } });
        setResults(data.results);
        setSearched(true);
        setOpen(true);
      } catch { setResults([]); setSearched(true); setOpen(true); } finally { setLoading(false); }
    }, 600);
    return () => clearTimeout(t);
  }, [query, mode]);

  const pick = async (addr) => {
    skipSearch.current = true;
    setOpen(false);
    if (addr.needs_resolve && addr.af_id) {
      setLoading(true);
      try {
        const { data } = await api.get(`/address/resolve`, { params: { id: addr.af_id } });
        skipSearch.current = true;
        setSelected(data);
        setQuery(data.label);
        onSelect(data);
      } catch {
        setSelected(null);
        setQuery(addr.label);
      } finally { setLoading(false); }
      return;
    }
    setSelected(addr);
    setQuery(addr.label);
    onSelect(addr);
  };

  return (
    <div className="space-y-1.5" ref={boxRef}>
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
        <Label className="text-sm font-semibold">{label}</Label>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          <button type="button" onClick={useMyLocation} disabled={locating} data-testid={`${testid}-use-gps`}
            title="Use my current location"
            className="flex items-center gap-1 rounded-md border border-kilted-gold/60 px-2 py-1 text-xs font-medium text-kilted-green transition-colors hover:bg-kilted-gold/10 disabled:opacity-50">
            {locating ? <Loader2 className="h-3 w-3 animate-spin" /> : <LocateFixed className="h-3 w-3 text-kilted-gold" />} My location
          </button>
          <div className="flex rounded-md border border-border p-0.5 text-xs">
          <button type="button" onClick={() => setMode("address")} data-testid={`${testid}-mode-address`}
            className={`rounded px-2 py-0.5 font-medium transition-colors ${mode === "address" ? "bg-kilted-green text-white" : "text-muted-foreground"}`}>
            <MapPin className="mr-1 inline h-3 w-3" />Address
          </button>
          <button type="button" onClick={() => setMode("business")} data-testid={`${testid}-mode-business`}
            className={`rounded px-2 py-0.5 font-medium transition-colors ${mode === "business" ? "bg-kilted-green text-white" : "text-muted-foreground"}`}>
            <Building2 className="mr-1 inline h-3 w-3" />Business
          </button>
          </div>
        </div>
      </div>
      <div className="relative">
        <Input
          data-testid={testid}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSelected(null); onQueryChange?.(e.target.value); }}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder || (mode === "business" ? "Search a business name (min 3 chars)…" : "Search street, town or postcode (min 3 chars)…")}
          autoComplete="off"
        />
        {loading && <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />}
        {selected && !loading && <Check className="absolute right-3 top-2.5 h-4 w-4 text-emerald-600" />}
        {open && results.length > 0 && (
          <ul className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-white shadow-lg" data-testid={`${testid}-results`}>
            {results.map((r) => (
              <li key={r.id}>
                <button type="button" onClick={() => pick(r)} data-testid={`${testid}-option-${r.id}`}
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-accent">
                  <span className="font-semibold text-kilted-green">{r.business || r.line1 || r.city || "Result"}</span>
                  <span className="text-xs text-muted-foreground">{r.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {open && results.length === 0 && searched && !loading && query.length >= 3 && !selected && (
          <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-white p-3 text-xs text-muted-foreground shadow-lg" data-testid={`${testid}-no-results`}>
            No matches found. Try a street name, town or postcode — the address lookup can also be briefly busy, so give it another moment.
          </div>
        )}
      </div>
      {selected && (
        <p className="text-xs text-muted-foreground" data-testid={`${testid}-selected`}>
          Selected: {selected.label}
        </p>
      )}
    </div>
  );
}
